/**
 * Phase 3 — step-up auth client helper.
 *
 * Public API:
 *
 *   await requireStepUp({
 *     action: 'wallet.transfer',
 *     amountZar: 1500,
 *     summary: { recipient_wallet_id: 'MW999', description: '...' },
 *   })
 *   // → returns { ticketId, authMethod } if step-up succeeded, or
 *   //   { ticketId: null, skipped: true } if no step-up was required.
 *   //   Throws if the user cancelled the prompt or assertion failed.
 *
 * Pair this at the top of a submit handler; on success pass the
 * `X-Step-Up-Ticket` header on the actual money-moving POST.
 */
import axios from 'axios';
import { b64uToBuf, bufToB64u } from './webauthn-client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://pay.mojomoney.africa';
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

/**
 * Build the same canonical summary string the backend hashes for ticket
 * binding. Mirror of backend/stepup_service.py:canonical_summary.
 *
 * Format: "Action: <action> | Amount: ZAR <amount>.2f | <key>=<value> | …"
 * Metadata keys sorted asc; None values dropped.
 */
export function canonicalSummary(action, amountZar, metadata = {}) {
  const parts = [
    `Action: ${action}`,
    `Amount: ZAR ${Number(amountZar).toFixed(2)}`,
  ];
  const keys = Object.keys(metadata).filter(k => metadata[k] != null).sort();
  for (const k of keys) {
    parts.push(`${k}=${metadata[k]}`);
  }
  return parts.join(' | ');
}

/**
 * Pre-flight: does this (action, amount) need step-up at all?
 * Cheap GET — call this before showing any prompt UI.
 */
export async function checkStepUpRequired(action, amountZar) {
  const r = await api.get('/stepup/required', { params: { action, amount: amountZar } });
  return r.data;   // { required, reason, threshold_zar, current_tier, methods? }
}

/**
 * Start + finish the WebAuthn step-up ceremony, return a ticket id.
 * The caller is responsible for surfacing whatever UI prompts the
 * user prior to and during the ceremony.
 */
async function webauthnStepUp({ action, amountZar, summary }) {
  // 1) Begin — server stores the challenge.
  const beginResp = await api.post('/stepup/challenge', {
    action,
    amount_zar: amountZar,
    summary: summary || {},
    method: 'webauthn',
  });
  const opts = beginResp.data.options;
  if (!opts || !opts.challenge) {
    throw new Error('No WebAuthn options returned from /stepup/challenge');
  }

  // 2) Run the browser's CTAP ceremony.
  const publicKey = {
    ...opts,
    challenge: b64uToBuf(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map(c => ({
      ...c, id: b64uToBuf(c.id),
    })),
  };
  const cred = await navigator.credentials.get({ publicKey });
  if (!cred) throw new Error('User cancelled the step-up prompt');

  // 3) Verify with the server, receive ticket.
  const assertion = {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON:    bufToB64u(cred.response.clientDataJSON),
      authenticatorData: bufToB64u(cred.response.authenticatorData),
      signature:         bufToB64u(cred.response.signature),
      userHandle:        cred.response.userHandle ? bufToB64u(cred.response.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults
      ? cred.getClientExtensionResults() : {},
  };
  const verifyResp = await api.post('/stepup/verify', {
    method: 'webauthn',
    action, amount_zar: amountZar, summary: summary || {},
    assertion,
  });
  return {
    ticketId:   verifyResp.data.ticket_id,
    authMethod: 'webauthn',
    expiresAt:  verifyResp.data.expires_at,
  };
}

/**
 * Start + finish the OTP step-up ceremony.
 * `promptOtp` is a (phoneHint) => Promise<string> the caller provides to
 * surface its own modal for collecting the 6-digit code.
 */
async function otpStepUp({ action, amountZar, summary, promptOtp }) {
  const beginResp = await api.post('/stepup/challenge', {
    action,
    amount_zar: amountZar,
    summary: summary || {},
    method: 'otp',
  });
  const phoneHint = beginResp.data.phone_hint;
  const otp = await promptOtp(phoneHint);
  if (!otp) throw new Error('User cancelled the OTP step-up');
  const verifyResp = await api.post('/stepup/verify', {
    method: 'otp',
    action, amount_zar: amountZar, summary: summary || {},
    otp,
  });
  return {
    ticketId:   verifyResp.data.ticket_id,
    authMethod: 'otp',
    expiresAt:  verifyResp.data.expires_at,
  };
}

/**
 * Full step-up entry point. Returns { ticketId, authMethod } on success,
 * { ticketId: null, skipped: true } when no step-up is required.
 *
 * options.promptOtp — required when WebAuthn is unavailable; the caller
 * passes a function that surfaces an OTP-collection modal and resolves
 * with the typed code.
 *
 * options.preferOtp — when true, skip the WebAuthn attempt entirely and
 * go straight to OTP (useful for an "I can't find my passkey" path).
 */
export async function requireStepUp({
  action,
  amountZar,
  summary = {},
  promptOtp = null,
  preferOtp = false,
}) {
  const check = await checkStepUpRequired(action, amountZar);
  if (!check.required) {
    return { ticketId: null, skipped: true };
  }
  const methods = check.methods || [];
  const hasWebAuthn = methods.includes('webauthn') && !preferOtp;
  const hasOtp = methods.includes('otp');

  if (hasWebAuthn) {
    try {
      return await webauthnStepUp({ action, amountZar, summary });
    } catch (e) {
      // If the user cancelled the prompt we re-throw; if it's a tech
      // failure AND OTP is available, transparently fall back.
      const msg = String(e?.message || e);
      const cancelled = msg.includes('cancel') || msg.includes('aborted');
      if (cancelled || !hasOtp || !promptOtp) throw e;
    }
  }
  if (hasOtp && promptOtp) {
    return await otpStepUp({ action, amountZar, summary, promptOtp });
  }
  throw new Error('No step-up method available — enrol a passkey in Settings.');
}
